import { z } from 'zod'

export const OauthTokenSchema = z
	.object({
		access_token: z.string(),
		expires_at: z.string()
	})
	.transform((input) => ({
		accessToken: input.access_token,
		expiresAt: input.expires_at
	}))
export type OauthToken = z.infer<typeof OauthTokenSchema>

export const OauthTokenCacheSchema = z.object({
	accessToken: z.string(),
	expiresAt: z.string()
})

export const OauthTokenResponseSchema = z.object({
	data: OauthTokenSchema
})
export type OauthTokenResponse = z.infer<typeof OauthTokenResponseSchema>

export const EhawkRequestSchema = z
	.object({
		email: z.string(),
		first_name: z.string(),
		last_name: z.string(),
		phone_number: z.string(),
		talon: z.string(),
		name: z.string(),
		ip: z.string(),
		apikey: z.string().optional(),
		campaignId: z.string().optional(),
		referrer: z.string().optional()
	})
	.transform((input) => ({
		email: input.email,
		firstname: input.first_name,
		lastname: input.last_name,
		phone: input.phone_number,
		talon: input.talon,
		username: input.name,
		ip: input.ip,
		apikey: input.apikey,
		campaign_id: input.campaignId,
		referrer: input.referrer
	}))
export type EhawkRequest = z.infer<typeof EhawkRequestSchema>

export const EhawkRiskSchema = z.object({
	risk: z.number()
})
export type EhawkRisk = z.infer<typeof EhawkRiskSchema>

export const EhawkResponseSchema = z
	.object({
		score: EhawkRiskSchema.optional(),
		status: z.number()
	})
	.transform((input) => ({
		risk: input?.score?.risk,
		status: input.status
	}))
export type EhawkResponse = z.infer<typeof EhawkResponseSchema>

export const BcAnalyticsCookieSchema = z.object({
	source: z.string().default('(direct)'),
	medium: z.string().default('(none)'),
	campaign: z.string().default('(none)'),
	adgroup: z.string().default('(none)'),
	keyword: z.string().default('(none)')
})
export type BcAnalyticsCookie = z.infer<typeof BcAnalyticsCookieSchema>

export const PrimaryContactSchema = z.object({
	first_name: z.string(),
	last_name: z.string(),
	email: z.string(),
	phone_number: z.string(),
	country: z.string(),
	district: z.string()
})
export type PrimaryContact = z.infer<typeof PrimaryContactSchema>

export const ContactlessCreateAccountRequestSchema = z.object({
	name: z.string(),
	email: z.string(),
	password: z.string()
})
export type ContactlessCreateAccountRequest = z.infer<typeof ContactlessCreateAccountRequestSchema>

export const CreateAccountRequestSchema = z
	.object({
		contactlessCreateAccountRequest: ContactlessCreateAccountRequestSchema,
		primary_contact: PrimaryContactSchema
	})
	.transform((input) => ({
		user_email: input.contactlessCreateAccountRequest.email,
		name: input.contactlessCreateAccountRequest.name,
		password: input.contactlessCreateAccountRequest.password,
		primary_contact: input.primary_contact
	}))
export type CreateAccountRequest = z.infer<typeof CreateAccountRequestSchema>

export const ContactlessCreateAccountNoPasswordRequestSchema = z.object({
	name: z.string(),
	email: z.string()
})
export type ContactlessCreateAccountNoPasswordRequest = z.infer<
	typeof ContactlessCreateAccountNoPasswordRequestSchema
>

export const CreateAccountNoPasswordRequestSchema = z
	.object({
		contactlessCreateAccountNoPasswordRequest: ContactlessCreateAccountNoPasswordRequestSchema,
		primary_contact: PrimaryContactSchema
	})
	.transform((input) => ({
		user_email: input.contactlessCreateAccountNoPasswordRequest.email,
		name: input.contactlessCreateAccountNoPasswordRequest.name,
		primary_contact: input.primary_contact
	}))
export type CreateAccountNoPasswordRequest = z.infer<typeof CreateAccountNoPasswordRequestSchema>

export const AccountSchema = z
	.object({
		id: z.string()
	})
	.transform((input) => ({
		uuid: input.id
	}))
export type Account = z.infer<typeof AccountSchema>

export const CreateAccountResponseSchema = z.object({
	data: AccountSchema
})
export type CreateAccountResponse = z.infer<typeof CreateAccountResponseSchema>

export const AuthHeadersSchema = z.object({
	'X-Auth-Client': z.string(),
	'X-Auth-Token': z.string()
})
export type AuthHeaders = z.infer<typeof AuthHeadersSchema>

export const CreateStoreRequestSchema = z.object({
	store_name: z.string(),
	country: z.string(),
	account_uuid: z.string(),
	plan_sku: z.string(),
	referral_id: z.string().optional(),
	region: z.string()
})
export type CreateStoreRequest = z.infer<typeof CreateStoreRequestSchema>

export const StoreSchema = z.object({
	id: z.string(),
	status: z.string(),
	primary_hostname: z.string().optional(),
	canonical_hostname: z.string().optional()
})
export type Store = z.infer<typeof StoreSchema>

export const StoreNotFoundSchema = z.object({
	status: z.number().default(404),
	title: z.string(),
	type: z.string(),
	errors: z.object({})
})
export type StoreNotFound = z.infer<typeof StoreNotFoundSchema>

export const StoreLoginSchema = z
	.object({
		'login-url': z.string()
	})
	.transform((input) => ({
		loginUrl: input['login-url']
	}))
export type StoreLogin = z.infer<typeof StoreLoginSchema>

export const CreateStoreResponseSchema = z.object({
	data: StoreSchema
})
export type CreateStoreResponse = z.infer<typeof CreateStoreResponseSchema>

export const GetStoreResponseSchema = z.object({
	data: StoreSchema
})
export type GetStoreResponse = z.infer<typeof GetStoreResponseSchema>

export const JwtAccountSchema = z.object({
	name: z.string(),
	uuid: z.string()
})
export type JwtAccount = z.infer<typeof JwtAccountSchema>

export const ExchangeJwtForAccountResponseSchema = z.object({
	data: JwtAccountSchema
})
export type ExchangeJwtForAccountResponse = z.infer<typeof ExchangeJwtForAccountResponseSchema>

export const ContinueCreateStoreRequestSchema = z
	.object({
		name: z.string(),
		country: z.string(),
		plan_sku: z.string(),
		referral_id: z.string().optional(),
		region: z.string()
	})
	.transform((input) => ({
		store_name: input.name,
		country: input.country,
		plan_sku: input.plan_sku,
		referral_id: input.referral_id,
		region: input.region
	}))
export type ContinueCreateStoreRequest = z.infer<typeof ContinueCreateStoreRequestSchema>

export const ExchangeJwtForAccountRequestSchema = z.object({
	jwt: z.string()
})
export type ExchangeJwtForAccountRequest = z.infer<typeof ExchangeJwtForAccountRequestSchema>

export const ContactSchema = z.object({
	email: z.string()
})
export type Contact = z.infer<typeof ContactSchema>

export const CreateStoreLoginSchema = z.object({
	user_email: z.string()
})
export type CreateStoreLogin = z.infer<typeof CreateStoreLoginSchema>

export const CreateStoreLoginResponseSchema = z.object({
	data: StoreLoginSchema
})
export type CreateStoreLoginResponse = z.infer<typeof CreateStoreLoginResponseSchema>

export const StoreInfoSchema = z
	.object({
		projectedAnnualRevenue: z.string(),
		plan_sku: z.string(),
		region: z.string()
	})
	.transform((input) => ({
		projectedAnnualRevenue: input.projectedAnnualRevenue,
		planSku: input.plan_sku,
		region: input.region
	}))

export type StoreInfo = z.infer<typeof StoreInfoSchema>

export const UserEligibleForNewAccountRequestSchema = z.object({
	token: OauthTokenSchema,
	emailAddress: z.string()
})
export type UserEligibleForNewAccountRequest = z.infer<
	typeof UserEligibleForNewAccountRequestSchema
>

export const UserEligibleForNewAccountSchema = z.object({
	eligibility_status: z.string()
})
export type UserEligibleForNewAccount = z.infer<typeof UserEligibleForNewAccountSchema>

export const UserEligibleForNewAccountResponseSchema = z
	.object({
		data: UserEligibleForNewAccountSchema
	})
	.transform((input) => ({
		eligibilityStatus: input.data.eligibility_status
	}))

export type UserEligibleForNewAccountResponse = z.infer<
	typeof UserEligibleForNewAccountResponseSchema
>

export const UserEligibleForNewStoreRequestSchema = z.object({
	token: OauthTokenSchema,
	emailAddress: z.string(),
	accountUuid: z.string()
})
export type UserEligibleForNewStoreRequest = z.infer<typeof UserEligibleForNewStoreRequestSchema>

export const UserEligibleForNewStoreSchema = z.object({
	eligible: z.boolean()
})
export type UserEligibleForNewStore = z.infer<typeof UserEligibleForNewStoreSchema>

export const UserEligibleForNewStoreResponseSchema = z
	.object({
		data: UserEligibleForNewStoreSchema
	})
	.transform((input) => ({
		eligible: input.data.eligible
	}))

export type UserEligibleForNewStoreResponse = z.infer<typeof UserEligibleForNewStoreResponseSchema>

export const SegmentPayloadSchema = BcAnalyticsCookieSchema.extend({
	formPage: z.string().default('')
})
export type SegmentPayload = z.infer<typeof SegmentPayloadSchema>

export const SegmentStepOneSchema = PrimaryContactSchema.merge(SegmentPayloadSchema)
	.omit({
		district: true,
		country: true
	})
	.extend({
		name: z.string(),
		projectedAnnualRevenue: z.string()
	})
	.transform(({ first_name, last_name, phone_number, name, ...rest }) => ({
		...rest,
		firstName: first_name,
		lastName: last_name,
		phone: phone_number,
		storeName: name
	}))
export type SegmentStepOne = z.infer<typeof SegmentStepOneSchema>

export const SegmentStepTwoSchema = SegmentPayloadSchema.extend({
	currentWebsiteUrl: z.string().default(''),
	industry: z.string().default(''),
	experience: z.string().default('')
}).transform(({ currentWebsiteUrl, ...rest }) => ({
	...rest,
	existingWebsiteUrl: currentWebsiteUrl
}))
export type SegmentStepTwo = z.infer<typeof SegmentStepTwoSchema>

export const SegmentAnonIdSchema = z
	.object({
		anonymousId: z.string()
	})
	.transform((input) => input.anonymousId)

export const GetStoreApiResponseSchema = z.object({
	canonicalHostname: z.string(),
	emailAddress: z.string(),
	primaryHostname: z.string(),
	storeId: z.string(),
	storeUrl: z.string(),
	storeUuid: z.string(),
	userCountry: z.string(),
	storeStatus: z.string()
})
export type GetStoreApiResponse = z.infer<typeof GetStoreApiResponseSchema>

export const CreateStoreApiResponseSchema = z.object({
	emailAddress: z.string(),
	accountUuid: z.string(),
	storeUuid: z.string()
})
export type CreateStoreApiResponse = z.infer<typeof CreateStoreApiResponseSchema>

export type ContinueCreationApiResponse = CreateStoreApiResponse

export const CreateStoreTraitsSchema = z
	.object({
		firstName: z.string(),
		lastName: z.string(),
		email: z.string(),
		phone: z.string(),
		storeName: z.string(),
		projectedAnnualRevenue: z.string()
	})
	.merge(SegmentPayloadSchema)
export type CreateStoreTraits = z.infer<typeof CreateStoreTraitsSchema>

export const SendCreateStoreEventRequestSchema = z.object({
	storeId: z.string(),
	planSku: z.string(),
	traits: CreateStoreTraitsSchema,
	trialsSecret: z.string().optional(),
	anonymousId: z.string().optional()
})
export type SendCreateStoreEventRequest = z.infer<typeof SendCreateStoreEventRequestSchema>

export const SessionCookieSchema = z.object({
	email: z.string(),
	newAccount: z.boolean().optional(),
	storeUuid: z.string()
})
export type SessionCookie = z.infer<typeof SessionCookieSchema>

export const CookieHeaderSchema = z.array(z.string()).default([])

export const RequestBodyCsrfSchema = z
	.object({
		_csrf: z.string().default('')
	})
	.transform((input) => input._csrf)

export const PoweredByErrorSchema = z
	.object({
		errors: z.record(z.string())
	})
	.transform((input) => {
		// Here we can map PBY error messages to user friendly ones
		if (input.errors['primary_contact.phone_number']) {
			input.errors['primary_contact.phone_number'] =
				'Invalid phone number provided. Enter a phone number including international dial code, e.g. +12345678901'
		}

		return input
	})

export const CreateStoreErrorSchema = z
	.object({
		error: z.record(z.string())
	})
	.transform((input) => Object.values(input.error))

export const ContinueCacheSchema = z
	.object({
		anonymousId: z.string(),
		name: z.string(),
		country: z.string(),
		plan_sku: z.string(),
		referral_id: z.string().optional(),
		projectedAnnualRevenue: z.string(),
		region: z.string()
	})
	.merge(PrimaryContactSchema.merge(SegmentPayloadSchema))
export type ContinueCache = z.infer<typeof ContinueCacheSchema>

export const RecaptchaTokenSchema = z
	.object({
		token: z.string()
	})
	.transform((input) => input.token)
export type RecaptchaToken = z.infer<typeof RecaptchaTokenSchema>

export const RecaptchRiskAnalysisSchema = z.object({
	score: z.number(),
	reasons: z.array(z.string())
})
export type RecaptchRiskAnalysis = z.infer<typeof RecaptchRiskAnalysisSchema>

export const RecaptchaAssessmentResponseSchema = z.object({
	riskAnalysis: RecaptchRiskAnalysisSchema.optional(),
	tokenProperties: z.object({
		valid: z.boolean(),
		invalidReason: z.string(),
		hostname: z.string(),
		action: z.string()
	})
})
export type RecaptchaAssessmentResponse = z.infer<typeof RecaptchaAssessmentResponseSchema>

export const TrialHealthCheckResponseSchema = z.object({
	healthy: z.boolean().default(true),
	unhealthy: z.string().array().default([])
})
export type TrialHealthCheckResponse = z.infer<typeof TrialHealthCheckResponseSchema>

export const IpHeadersSchema = z
	.object({
		'x-gcp-connecting-ip': z.string().optional(),
		'true-client-ip': z.string().optional()
	})
	.transform((input) => ({
		gcpConnectingIp: input['x-gcp-connecting-ip'],
		trueClientIp: input['true-client-ip']
	}))
export type IpHeaders = z.infer<typeof IpHeadersSchema>
